import {
    createUserWithEmailAndPassword,
    signOut as firebaseSignOut,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    type User,
    updateProfile,
} from 'firebase/auth';
import { useContext } from 'react';
import { AuthContext } from '../../components/auth/AuthProvider';
import { getFirebaseAuth } from '../../utils/firebase/firebase';

/**
 * Hook that provides authentication state and methods
 */
export const useAuth = () => {
    const { user, loading, error } = useContext(AuthContext);

    /**
     * Sign in with email and password
     */
    const signIn = async (email: string, password: string) => {
        try {
            return await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
        } catch (error) {
            console.error('Error signing in:', error);
            throw error;
        }
    };

    /**
     * Sign up with email and password
     */
    const signUp = async (email: string, password: string, displayName?: string) => {
        try {
            const result = await createUserWithEmailAndPassword(getFirebaseAuth(), email, password);

            // Update profile if displayName is provided
            if (displayName && result.user) {
                await updateProfile(result.user, { displayName });
            }

            return result;
        } catch (error) {
            console.error('Error signing up:', error);
            throw error;
        }
    };

    /**
     * Sign out
     */
    const signOut = async () => {
        try {
            await firebaseSignOut(getFirebaseAuth());
        } catch (error) {
            console.error('Error signing out:', error);
            throw error;
        }
    };

    /**
     * Reset password
     */
    const resetPassword = async (email: string) => {
        try {
            await sendPasswordResetEmail(getFirebaseAuth(), email);
        } catch (error) {
            console.error('Error resetting password:', error);
            throw error;
        }
    };

    /**
     * Update user profile
     */
    const updateUserProfile = async (user: User, profile: { displayName?: string; photoURL?: string }) => {
        try {
            await updateProfile(user, profile);
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    };

    return {
        user,
        loading,
        error,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updateUserProfile,
        isAuthenticated: !!user,
    };
};
